import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BellRing,
  Check,
  FileText,
  KeyRound,
  Pencil,
  Power,
  RefreshCw,
  Server,
  Shield,
  Wrench,
  X,
} from 'lucide-react';
import {
  ADMIN_AGENT_TOKEN_STORAGE_KEY,
  createAdminHealthcheckTarget,
  fetchAdminContainerLogs,
  fetchAdminContainers,
  fetchAdminHealthcheckTargets,
  fetchAdminServiceAliases,
  fetchAdminIncidents,
  getAdminApiBaseUrl,
  restartAdminContainer,
  runAdminHealthcheckTargetNow,
  updateAdminServiceAliases,
  updateAdminHealthcheckTarget,
} from '../services/api';
import theme from '../theme';
import {
  ADMIN_TUNNEL_COMMAND,
  getAdminTunnelInstructions,
  LOG_VIEWER_CLASS,
  getRestartConfirmationPhrase,
  getServiceBadgeClass,
  normalizeFailureThreshold,
  normalizeHealthcheckInterval,
  normalizeHealthcheckTimeout,
  normalizeLogTail,
} from './AdminPanelUtils';

const DEFAULT_TARGET_DRAFT = {
  name: '',
  url: '',
  failureThreshold: '3',
  intervalSeconds: '15',
  timeoutSeconds: '5',
  graceWeekdaysUtc: [],
  graceTimeWindowsUtc: [],
  mitigationKind: 'restart_services',
  restartServices: [],
  customCommand: '',
};
const HEALTHCHECKS_AUTO_REFRESH_MS = 3000;
const SERVICES_AUTO_REFRESH_MS = 3000;
const UTC_WEEKDAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const toMitigationPayload = (draft) => {
  if (draft.mitigationKind === 'custom_command') {
    return {
      kind: 'custom_command',
      command: draft.customCommand,
    };
  }
  return {
    kind: 'restart_services',
    services: draft.restartServices,
  };
};

const AdminPanel = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(ADMIN_AGENT_TOKEN_STORAGE_KEY) || '');
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [logs, setLogs] = useState('');
  const [tail, setTail] = useState(200);
  const [confirmation, setConfirmation] = useState('');
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const [targets, setTargets] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loadingHealthchecks, setLoadingHealthchecks] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);
  const [runningHealthcheckNow, setRunningHealthcheckNow] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [targetDraft, setTargetDraft] = useState(DEFAULT_TARGET_DRAFT);
  const [isTargetEditorOpen, setIsTargetEditorOpen] = useState(false);
  const [serviceAliases, setServiceAliases] = useState({});
  const [editingAliasService, setEditingAliasService] = useState('');
  const [aliasDraft, setAliasDraft] = useState('');
  const [graceWindowDraft, setGraceWindowDraft] = useState({ startUtc: '00:00', endUtc: '01:00' });

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tunnelReachable, setTunnelReachable] = useState(true);
  const [checkingTunnel, setCheckingTunnel] = useState(true);
  const [copyNotice, setCopyNotice] = useState('');
  const healthchecksRequestInFlightRef = useRef(false);
  const servicesRequestInFlightRef = useRef(false);
  const aliasEditorRef = useRef(null);

  const adminBaseUrl = useMemo(() => getAdminApiBaseUrl(), []);
  const selectedStatus = services.find((service) => service.name === selectedService);
  const selectedServiceAlias = serviceAliases[selectedService] || selectedService;
  const expectedConfirmation = selectedService ? getRestartConfirmationPhrase(selectedService) : '';
  const selectedTarget = targets.find((target) => target.id === selectedTargetId) || null;
  const canCallAgent = Boolean(token.trim());
  const selectableServiceNames = useMemo(() => {
    const names = new Set(services.map((service) => service.name));
    targets.forEach((target) => {
      if (target.name) names.add(target.name);
      (target?.mitigation?.services || []).forEach((serviceName) => names.add(serviceName));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [services, targets]);
  const watchedStatusByService = useMemo(() => {
    const byService = {};
    targets.forEach((target) => {
      if (!target?.name) return;
      const current = byService[target.name];
      const next = target.latest_status || 'unknown';
      if (current === 'unhealthy' || next === 'unhealthy') {
        byService[target.name] = 'unhealthy';
        return;
      }
      if (current === 'healthy' || next === 'healthy') {
        byService[target.name] = 'healthy';
        return;
      }
      byService[target.name] = 'unknown';
    });
    return byService;
  }, [targets]);
  const hasValidMitigationInput = targetDraft.mitigationKind === 'custom_command'
    ? Boolean(targetDraft.customCommand.trim())
    : targetDraft.restartServices.length > 0;
  const canSaveTarget = (
    canCallAgent
    && !savingTarget
    && Boolean(targetDraft.name.trim())
    && Boolean(targetDraft.url.trim())
    && Boolean(String(targetDraft.failureThreshold).trim())
    && Boolean(String(targetDraft.intervalSeconds).trim())
    && Boolean(String(targetDraft.timeoutSeconds).trim())
    && hasValidMitigationInput
  );

  useEffect(() => {
    if (!editingAliasService || !aliasEditorRef.current) return;
    aliasEditorRef.current.focus();
    const length = aliasEditorRef.current.value?.length || 0;
    aliasEditorRef.current.setSelectionRange(length, length);
  }, [editingAliasService]);

  const saveToken = (nextToken) => {
    setToken(nextToken);
    if (nextToken.trim()) {
      sessionStorage.setItem(ADMIN_AGENT_TOKEN_STORAGE_KEY, nextToken);
    } else {
      sessionStorage.removeItem(ADMIN_AGENT_TOKEN_STORAGE_KEY);
    }
  };

  const getServiceAlias = (serviceName) => serviceAliases[serviceName] || serviceName;

  const startAliasEdit = (serviceName) => {
    setEditingAliasService(serviceName);
    setAliasDraft(getServiceAlias(serviceName));
  };

  const saveServiceAlias = async (serviceName) => {
    if (!canCallAgent) return;
    const trimmed = aliasDraft.trim();
    const nextAliases = { ...serviceAliases };
    if (!trimmed || trimmed === serviceName) {
      delete nextAliases[serviceName];
    } else {
      nextAliases[serviceName] = trimmed;
    }

    const payload = await withErrorHandling(() => updateAdminServiceAliases(token, nextAliases));
    if (payload?.aliases) {
      setServiceAliases(payload.aliases);
    }
    setEditingAliasService('');
    setAliasDraft('');
  };

  const cancelAliasEdit = () => {
    setEditingAliasService('');
    setAliasDraft('');
  };

  const withErrorHandling = async (action) => {
    setError('');
    setNotice('');
    try {
      return await action();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || err.message || 'Request failed');
      return null;
    }
  };

  const loadServices = async () => {
    if (!canCallAgent || servicesRequestInFlightRef.current) return;
    servicesRequestInFlightRef.current = true;
    setLoadingServices(true);
    const payload = await withErrorHandling(async () => {
      const [servicesData, aliasesData] = await Promise.all([
        fetchAdminContainers(token),
        fetchAdminServiceAliases(token),
      ]);
      return { servicesData, aliasesData };
    });
    if (payload?.servicesData?.services) {
      setServices(payload.servicesData.services);
      setSelectedService((current) => current || payload.servicesData.services[0]?.name || '');
    }
    if (payload?.aliasesData?.aliases) {
      setServiceAliases(payload.aliasesData.aliases);
    }
    setLoadingServices(false);
    servicesRequestInFlightRef.current = false;
  };

  const loadHealthchecks = useCallback(async () => {
    if (!canCallAgent || healthchecksRequestInFlightRef.current) return;
    healthchecksRequestInFlightRef.current = true;
    setLoadingHealthchecks(true);
    const payload = await withErrorHandling(async () => {
      const [targetData, incidentData] = await Promise.all([
        fetchAdminHealthcheckTargets(token),
        fetchAdminIncidents(token),
      ]);
      return { targetData, incidentData };
    });

    if (payload?.targetData) {
      const nextTargets = payload.targetData.targets || [];
      setTargets(nextTargets);
      setSelectedTargetId((current) => current || nextTargets[0]?.id || '');
    }
    if (payload?.incidentData) {
      setIncidents(payload.incidentData.incidents || []);
    }
    setLoadingHealthchecks(false);
    healthchecksRequestInFlightRef.current = false;
  }, [canCallAgent, token]);

  const loadLogs = async () => {
    if (!selectedService || !canCallAgent) return;
    setLoadingLogs(true);
    const payload = await withErrorHandling(() => fetchAdminContainerLogs(
      selectedService,
      token,
      { tail: normalizeLogTail(tail) },
    ));
    if (payload) {
      setLogs(payload.logs || '');
      setTail(payload.tail || normalizeLogTail(tail));
      setNotice(payload.truncated ? 'Logs truncated' : '');
    }
    setLoadingLogs(false);
  };

  const restartService = async () => {
    if (!selectedService || !canCallAgent) return;
    setRestarting(true);
    const payload = await withErrorHandling(() => restartAdminContainer(
      selectedService,
      token,
      confirmation,
    ));
    if (payload?.ok) {
      setNotice(`${selectedService} restarted`);
      setConfirmation('');
      await loadServices();
    }
    setRestarting(false);
  };

  const saveTarget = async () => {
    if (!canCallAgent) return;
    setSavingTarget(true);
    const basePayload = {
      name: targetDraft.name,
      url: targetDraft.url.trim(),
      failure_threshold: normalizeFailureThreshold(targetDraft.failureThreshold),
      interval_seconds: normalizeHealthcheckInterval(targetDraft.intervalSeconds),
      timeout_seconds: normalizeHealthcheckTimeout(targetDraft.timeoutSeconds, targetDraft.intervalSeconds),
      grace_weekdays_utc: targetDraft.graceWeekdaysUtc,
      grace_time_windows_utc: targetDraft.graceTimeWindowsUtc,
      mitigation: toMitigationPayload(targetDraft),
    };

    const payload = await withErrorHandling(async () => {
      if (selectedTargetId) {
        return updateAdminHealthcheckTarget(selectedTargetId, token, basePayload);
      }
      return createAdminHealthcheckTarget(token, basePayload);
    });

    if (payload?.id) {
      setNotice(selectedTargetId ? 'Target updated' : 'Target created');
      setTargetDraft(DEFAULT_TARGET_DRAFT);
      setGraceWindowDraft({ startUtc: '00:00', endUtc: '01:00' });
      await loadHealthchecks();
    }
    setSavingTarget(false);
  };

  const loadTargetForEdit = (target) => {
    setSelectedTargetId(target.id);
    setIsTargetEditorOpen(true);
    setTargetDraft({
      name: target.name,
      url: target.url || '',
      failureThreshold: String(target.failure_threshold),
      intervalSeconds: String(target.interval_seconds ?? 15),
      timeoutSeconds: String(target.timeout_seconds ?? 5),
      graceWeekdaysUtc: target.grace_weekdays_utc || [],
      graceTimeWindowsUtc: target.grace_time_windows_utc || [],
      mitigationKind: target.mitigation.kind,
      restartServices: target.mitigation.services || [],
      customCommand: target.mitigation.command || '',
    });
  };

  const toggleRestartServiceSelection = (serviceName) => {
    setTargetDraft((current) => {
      const alreadySelected = current.restartServices.includes(serviceName);
      const nextRestartServices = alreadySelected
        ? current.restartServices.filter((entry) => entry !== serviceName)
        : [...current.restartServices, serviceName];
      return {
        ...current,
        restartServices: nextRestartServices,
      };
    });
  };

  const toggleGraceWeekdaySelection = (weekday) => {
    setTargetDraft((current) => {
      const alreadySelected = current.graceWeekdaysUtc.includes(weekday);
      const nextDays = alreadySelected
        ? current.graceWeekdaysUtc.filter((entry) => entry !== weekday)
        : [...current.graceWeekdaysUtc, weekday];
      const sortedDays = UTC_WEEKDAY_OPTIONS
        .map((entry) => entry.value)
        .filter((entry) => nextDays.includes(entry));
      return {
        ...current,
        graceWeekdaysUtc: sortedDays,
      };
    });
  };

  const addGraceTimeWindow = () => {
    const startUtc = String(graceWindowDraft.startUtc || '').trim();
    const endUtc = String(graceWindowDraft.endUtc || '').trim();
    if (!startUtc || !endUtc) {
      setError('Grace time window requires both start and end UTC times');
      return;
    }
    if (startUtc === endUtc) {
      setError('Grace time window start and end cannot be equal');
      return;
    }
    setTargetDraft((current) => {
      const exists = current.graceTimeWindowsUtc.some((window) => window.start_utc === startUtc && window.end_utc === endUtc);
      if (exists) return current;
      return {
        ...current,
        graceTimeWindowsUtc: [...current.graceTimeWindowsUtc, { start_utc: startUtc, end_utc: endUtc }],
      };
    });
    setError('');
  };

  const removeGraceTimeWindow = (index) => {
    setTargetDraft((current) => ({
      ...current,
      graceTimeWindowsUtc: current.graceTimeWindowsUtc.filter((_, entryIndex) => entryIndex !== index),
    }));
  };

  const runCheckNow = async () => {
    if (!selectedTargetId) return;
    setRunningHealthcheckNow(true);
    const payload = await withErrorHandling(() => runAdminHealthcheckTargetNow(selectedTargetId, token));

    if (payload?.ok) {
      setNotice(payload.incident_opened ? `Incident opened: ${payload.incident_id}` : `Status: ${payload.status}`);
      await loadHealthchecks();
    }
    setRunningHealthcheckNow(false);
  };

  const clearTargetSelection = () => {
    setSelectedTargetId('');
    setTargetDraft(DEFAULT_TARGET_DRAFT);
    setGraceWindowDraft({ startUtc: '00:00', endUtc: '01:00' });
    setIsTargetEditorOpen(true);
  };

  const copyTunnelCommand = async () => {
    try {
      await navigator.clipboard.writeText(ADMIN_TUNNEL_COMMAND);
      setCopyNotice('Command copied');
    } catch (err) {
      setCopyNotice('Could not copy automatically');
    }
  };

  const checkTunnelReachability = async () => {
    setCheckingTunnel(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1500);
      await fetch(`${adminBaseUrl}/healthcheck`, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      // In no-cors mode any successful network round-trip resolves with an opaque response.
      setTunnelReachable(true);
    } catch (err) {
      setTunnelReachable(false);
    } finally {
      setCheckingTunnel(false);
    }
  };

  useEffect(() => {
    checkTunnelReachability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminBaseUrl]);

  useEffect(() => {
    if (!tunnelReachable) return;
    loadServices();
    loadHealthchecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnelReachable]);

  useEffect(() => {
    if (!tunnelReachable || !canCallAgent) return undefined;
    const intervalId = window.setInterval(() => {
      loadHealthchecks();
    }, HEALTHCHECKS_AUTO_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [tunnelReachable, canCallAgent, loadHealthchecks]);

  useEffect(() => {
    if (!tunnelReachable || !canCallAgent) return undefined;
    const intervalId = window.setInterval(() => {
      loadServices();
    }, SERVICES_AUTO_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [tunnelReachable, canCallAgent, token]);

  if (checkingTunnel || !tunnelReachable) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <div className="w-full max-w-lg rounded-xl border border-olive-200 bg-white p-6 text-center">
          <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Admin Tunnel Required</h1>
          <p className={`mt-3 text-sm ${theme.textSecondary}`}>
            {getAdminTunnelInstructions()}
          </p>
          <button
            type="button"
            onClick={copyTunnelCommand}
            className={`mt-5 inline-flex h-11 items-center justify-center rounded-lg px-5 text-sm font-semibold ${theme.btnPrimary}`}
          >
            Copy tunnel command
          </button>
          <div className="mt-4 rounded-lg border border-olive-200 bg-olive-50 px-3 py-2 text-left">
            <div className={`text-xs font-semibold ${theme.textSecondary}`}>Manual copy:</div>
            <div className="mt-1 overflow-x-auto whitespace-nowrap font-mono text-xs text-olive-800">
              {ADMIN_TUNNEL_COMMAND}
            </div>
          </div>
          {copyNotice && (
            <div className="mt-3 text-sm font-semibold text-emerald-700">{copyNotice}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg}`}>
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 border-b border-olive-100 pb-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className={`flex items-center gap-2 text-sm font-semibold ${theme.textSecondary}`}>
              <Shield size={18} />
              <span>{adminBaseUrl}</span>
            </div>
            <h1 className={`mt-2 text-2xl font-bold ${theme.textPrimary}`}>Container Admin</h1>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-olive-400" />
              <input
                type="password"
                name="admin-agent-token"
                autoComplete="new-password"
                value={token}
                onChange={(event) => saveToken(event.target.value)}
                placeholder="Admin token"
                className={`${theme.input} h-10 w-full pl-9 pr-3 text-sm sm:w-72`}
              />
            </div>
            <button
              type="button"
              onClick={async () => {
                await loadServices();
                await loadHealthchecks();
              }}
              disabled={!canCallAgent || loadingServices || loadingHealthchecks}
              title="Refresh"
              className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold ${theme.btnPrimary} disabled:opacity-50`}
            >
              <RefreshCw size={16} className={(loadingServices || loadingHealthchecks) ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            <AlertTriangle size={17} />
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {notice}
          </div>
        )}

        <div className="grid gap-5 xl:grid-cols-[minmax(250px,320px)_1fr]">
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Server size={18} className="text-olive-500" />
              <h2 className={`text-base font-bold ${theme.textPrimary}`}>Services</h2>
            </div>
            <div className="overflow-hidden rounded-lg border border-olive-200">
              {services.map((service) => {
                const isEditing = editingAliasService === service.name;
                const alias = getServiceAlias(service.name);
                return (
                  <div
                    key={service.name}
                    className={`flex items-start justify-between gap-3 border-b border-olive-100 px-4 py-3 last:border-b-0 ${
                      selectedService === service.name ? 'bg-olive-50' : 'bg-white hover:bg-olive-50/70'
                    }`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedService(service.name);
                        setConfirmation('');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedService(service.name);
                          setConfirmation('');
                        }
                      }}
                      className="min-w-0 flex-1 text-left"
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                          <textarea
                            ref={aliasEditorRef}
                            rows={1}
                            aria-label={`Alias for ${service.name}`}
                            name={`service-alias-${service.name}`}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-form-type="other"
                            data-1p-ignore="true"
                            data-lpignore="true"
                            data-bwignore="true"
                            value={aliasDraft}
                            onChange={(event) => setAliasDraft(event.target.value)}
                            onKeyDown={(event) => {
                              event.stopPropagation();
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                saveServiceAlias(service.name);
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelAliasEdit();
                              }
                            }}
                            className={`${theme.input} h-8 w-full resize-none overflow-hidden px-2 py-1 text-sm leading-6`}
                          />
                          <button type="button" className="text-emerald-700" onClick={() => saveServiceAlias(service.name)} title="Save alias">
                            <Check size={14} />
                          </button>
                          <button type="button" className="text-red-700" onClick={cancelAliasEdit} title="Cancel alias edit">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`truncate text-sm font-semibold ${theme.textPrimary}`}>{alias}</span>
                            <button
                              type="button"
                              className="shrink-0 text-olive-500 hover:text-olive-700"
                              onClick={(event) => {
                                event.stopPropagation();
                                startAliasEdit(service.name);
                              }}
                              title="Edit alias"
                            >
                              <Pencil size={13} />
                            </button>
                          </div>
                          <div className="truncate text-[11px] font-normal text-gray-500">{service.name}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getServiceBadgeClass(service.state, service.health)}`}>
                        docker: {service.health || service.state}
                      </span>
                      {watchedStatusByService[service.name] && (
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${
                          watchedStatusByService[service.name] === 'healthy'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : watchedStatusByService[service.name] === 'unhealthy'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}>
                          watch: {watchedStatusByService[service.name]}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {services.length === 0 && (
                <div className={`px-4 py-8 text-center text-sm ${theme.textMuted}`}>
                  No services loaded
                </div>
              )}
            </div>
          </section>

          <section className="space-y-5">
            <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
              <div className="rounded-lg border border-olive-200 bg-white p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className={`text-lg font-bold ${theme.textPrimary}`}>{selectedServiceAlias || 'Select a service'}</h2>
                    {selectedStatus && (
                      <p className={`mt-1 text-sm ${theme.textSecondary}`}>
                        <span className="font-medium text-olive-700">{selectedStatus.name}</span> - {selectedStatus.container || 'No container'} - {selectedStatus.status}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={tail}
                      onChange={(event) => setTail(event.target.value)}
                      className={`${theme.input} h-10 w-24 px-3 text-sm`}
                    />
                    <button
                      type="button"
                      onClick={loadLogs}
                      disabled={!selectedService || !canCallAgent || loadingLogs}
                      title="Logs"
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold ${theme.btnSecondary} disabled:opacity-50`}
                    >
                      <FileText size={16} />
                      Logs
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-red-200 bg-white p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <Power size={18} />
                  <h2 className="text-base font-bold">Restart</h2>
                </div>
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder={expectedConfirmation || 'restart service'}
                  className={`${theme.input} mt-3 h-10 w-full px-3 text-sm`}
                />
                <button
                  type="button"
                  onClick={restartService}
                  disabled={
                    !selectedService ||
                    !canCallAgent ||
                    restarting ||
                    confirmation !== expectedConfirmation
                  }
                  className={`mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold ${theme.btnDanger} disabled:opacity-50`}
                >
                  <Power size={16} />
                  {restarting ? 'Restarting' : 'Restart'}
                </button>
              </div>
            </div>

            <pre className={LOG_VIEWER_CLASS}>
              {loadingLogs ? 'Loading logs...' : logs || 'No logs loaded'}
            </pre>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="flex h-full flex-col gap-4 rounded-lg border border-olive-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Activity size={18} className="text-olive-500" />
                    <h2 className={`text-base font-bold ${theme.textPrimary}`}>Healthcheck Watch List</h2>
                  </div>
                  <button
                    type="button"
                    onClick={clearTargetSelection}
                    className={`rounded-lg px-2 py-1 text-xs font-semibold ${theme.btnSecondary}`}
                  >
                    New Target
                  </button>
                </div>

                <div className="max-h-44 space-y-2 overflow-auto rounded-lg border border-olive-100 p-2">
                  {targets.map((target) => (
                    <button
                      key={target.id}
                      type="button"
                      onClick={() => loadTargetForEdit(target)}
                      className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                        selectedTargetId === target.id ? 'border-olive-400 bg-olive-50' : 'border-olive-200 bg-white'
                      }`}
                    >
                      <div className={`font-semibold ${theme.textPrimary}`}>{target.name}</div>
                      <div className={`text-xs ${theme.textSecondary}`}>
                        {target.latest_status} | failures {target.consecutive_failures}/{target.failure_threshold}
                      </div>
                      <div className={`mt-1 truncate text-[11px] ${theme.textSecondary}`}>
                        every {target.interval_seconds}s, timeout {target.timeout_seconds}s
                      </div>
                      {((target.grace_weekdays_utc || []).length > 0 || (target.grace_time_windows_utc || []).length > 0) && (
                        <div className={`mt-1 text-[11px] ${theme.textSecondary}`}>
                          grace UTC:
                          {(target.grace_weekdays_utc || []).length > 0 && (
                            <span> days {target.grace_weekdays_utc.join(', ')}</span>
                          )}
                          {(target.grace_time_windows_utc || []).length > 0 && (
                            <span> windows {(target.grace_time_windows_utc || []).map((window) => `${window.start_utc}-${window.end_utc}`).join(', ')}</span>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                  {targets.length === 0 && (
                    <div className={`text-sm ${theme.textMuted}`}>No targets configured</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsTargetEditorOpen((current) => !current)}
                  className={`inline-flex h-10 items-center justify-center rounded-lg text-sm font-semibold ${theme.btnSecondary}`}
                >
                  {isTargetEditorOpen ? 'Hide target editor' : 'Show target editor'}
                </button>
                {isTargetEditorOpen && (
                  <>
                    <div className="grid gap-2">
                      <select
                        value={targetDraft.name}
                        onChange={(event) => setTargetDraft((current) => ({ ...current, name: event.target.value }))}
                        className={`${theme.input} h-10 px-3 text-sm`}
                      >
                        <option value="">Select target service</option>
                        {selectableServiceNames.map((serviceName) => (
                          <option key={`target-name-${serviceName}`} value={serviceName}>
                            {serviceName}
                          </option>
                        ))}
                      </select>
                      <input
                        value={targetDraft.url}
                        onChange={(event) => setTargetDraft((current) => ({ ...current, url: event.target.value }))}
                        placeholder="https://mozart-trader.com/api/health/healthcheck-ib-client"
                        className={`${theme.input} h-10 px-3 text-sm`}
                      />
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={targetDraft.failureThreshold}
                        onChange={(event) => setTargetDraft((current) => ({ ...current, failureThreshold: event.target.value }))}
                        placeholder="Failure threshold"
                        className={`${theme.input} h-10 px-3 text-sm`}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          min="5"
                          max="3600"
                          value={targetDraft.intervalSeconds}
                          onChange={(event) => setTargetDraft((current) => ({ ...current, intervalSeconds: event.target.value }))}
                          placeholder="Interval (sec)"
                          className={`${theme.input} h-10 px-3 text-sm`}
                        />
                        <input
                          type="number"
                          min="1"
                          max="120"
                          value={targetDraft.timeoutSeconds}
                          onChange={(event) => setTargetDraft((current) => ({ ...current, timeoutSeconds: event.target.value }))}
                          placeholder="Timeout (sec)"
                          className={`${theme.input} h-10 px-3 text-sm`}
                        />
                      </div>
                      <div className="rounded-lg border border-olive-200 p-2">
                        <div className={`text-xs font-semibold ${theme.textSecondary}`}>Grace weekdays (UTC, full day)</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {UTC_WEEKDAY_OPTIONS.map((day) => (
                            <label key={`grace-weekday-${day.value}`} className="inline-flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={targetDraft.graceWeekdaysUtc.includes(day.value)}
                                onChange={() => toggleGraceWeekdaySelection(day.value)}
                              />
                              <span className={theme.textPrimary}>{day.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-lg border border-olive-200 p-2">
                        <div className={`text-xs font-semibold ${theme.textSecondary}`}>Grace time windows (UTC)</div>
                        <div className="mt-2 grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                          <input
                            type="time"
                            value={graceWindowDraft.startUtc}
                            onChange={(event) => setGraceWindowDraft((current) => ({ ...current, startUtc: event.target.value }))}
                            className={`${theme.input} h-9 px-2 text-sm`}
                          />
                          <input
                            type="time"
                            value={graceWindowDraft.endUtc}
                            onChange={(event) => setGraceWindowDraft((current) => ({ ...current, endUtc: event.target.value }))}
                            className={`${theme.input} h-9 px-2 text-sm`}
                          />
                          <button
                            type="button"
                            onClick={addGraceTimeWindow}
                            className={`inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-semibold ${theme.btnSecondary}`}
                          >
                            Add
                          </button>
                        </div>
                        <div className="mt-2 max-h-24 space-y-1 overflow-auto">
                          {targetDraft.graceTimeWindowsUtc.map((window, index) => (
                            <div key={`grace-window-${window.start_utc}-${window.end_utc}`} className="flex items-center justify-between rounded border border-olive-100 bg-olive-50 px-2 py-1 text-xs">
                              <span className={theme.textPrimary}>{window.start_utc} - {window.end_utc}</span>
                              <button
                                type="button"
                                onClick={() => removeGraceTimeWindow(index)}
                                className="text-red-700"
                                title="Remove grace time window"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                          {targetDraft.graceTimeWindowsUtc.length === 0 && (
                            <div className={`text-xs ${theme.textMuted}`}>No grace time windows</div>
                          )}
                        </div>
                      </div>
                      <select
                        value={targetDraft.mitigationKind}
                        onChange={(event) => setTargetDraft((current) => ({ ...current, mitigationKind: event.target.value }))}
                        className={`${theme.input} h-10 px-3 text-sm`}
                      >
                        <option value="restart_services">Restart services</option>
                        <option value="custom_command">Custom bash command</option>
                      </select>
                      {targetDraft.mitigationKind === 'restart_services' ? (
                        <div className="max-h-32 space-y-2 overflow-auto rounded-lg border border-olive-200 p-2">
                          {selectableServiceNames.map((serviceName) => (
                            <label key={`restart-service-${serviceName}`} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={targetDraft.restartServices.includes(serviceName)}
                                onChange={() => toggleRestartServiceSelection(serviceName)}
                              />
                              <span className={theme.textPrimary}>{serviceName}</span>
                            </label>
                          ))}
                          {selectableServiceNames.length === 0 && (
                            <div className={`text-xs ${theme.textMuted}`}>Load services to choose restart targets</div>
                          )}
                        </div>
                      ) : (
                        <input
                          value={targetDraft.customCommand}
                          onChange={(event) => setTargetDraft((current) => ({ ...current, customCommand: event.target.value }))}
                          placeholder="docker compose ... restart backend"
                          className={`${theme.input} h-10 px-3 text-sm`}
                        />
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={saveTarget}
                      disabled={!canSaveTarget}
                      className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold ${theme.btnPrimary} disabled:opacity-50`}
                    >
                      <Wrench size={16} />
                      {savingTarget ? 'Saving...' : selectedTargetId ? 'Update Target' : 'Create Target'}
                    </button>

                    {selectedTarget && (
                      <div className={`rounded-md border border-olive-200 bg-olive-50 p-2 text-xs ${theme.textSecondary}`}>
                        Watch URL: <span className="font-mono break-all">{selectedTarget.url}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-4 rounded-lg border border-olive-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <BellRing size={18} className="text-amber-500" />
                  <h2 className={`text-base font-bold ${theme.textPrimary}`}>Incidents</h2>
                </div>

                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={runCheckNow}
                    disabled={!selectedTargetId || !canCallAgent || runningHealthcheckNow}
                    className={`inline-flex h-10 items-center justify-center rounded-lg text-sm font-semibold ${theme.btnSecondary} disabled:opacity-50`}
                  >
                    {runningHealthcheckNow ? 'Checking...' : 'Run check now'}
                  </button>
                </div>

                <div data-testid="incidents-list" className="min-h-0 flex-1 space-y-2 overflow-auto rounded-lg border border-olive-100 p-2">
                  {incidents.map((incident) => (
                    <div key={incident.id} className="rounded-md border border-olive-200 bg-white p-2 text-sm">
                      <div className={`font-semibold ${theme.textPrimary}`}>{incident.target_name}</div>
                      <div className={`text-xs ${theme.textSecondary}`}>
                        {incident.status} | opened {incident.opened_at}
                      </div>
                      {incident.mitigation && (
                        <div className="mt-1 text-xs text-olive-700">
                          mitigation: {incident.mitigation.kind} ({incident.mitigation.success ? 'success' : 'failed'})
                        </div>
                      )}
                      {(incident.suggested_tactics || []).slice(0, 2).map((tactic) => (
                        <div key={`${incident.id}-${tactic}`} className="mt-1 text-xs text-amber-700">
                          - {tactic}
                        </div>
                      ))}
                    </div>
                  ))}
                  {incidents.length === 0 && (
                    <div className={`text-sm ${theme.textMuted}`}>
                      {loadingHealthchecks ? 'Loading incidents...' : 'No incidents yet'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
